import { useEffect, useContext, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DroppableProps, DropResult } from 'react-beautiful-dnd';
import { Form } from '@/components/ui/form';
import cn from 'classnames';
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from '@/components/ui/button';
import { MessageCombobox, type ValueRendererProps, type Option } from '@src/components/combobox/Combobox';
import { Badge } from "@/components/ui/badge";
import {Cross1Icon, LinkBreak2Icon} from "@radix-ui/react-icons";
import { z } from 'zod';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import type {
  CustomNodeData,
  NodeToRenderProps,
  ConditionStatement,
  NodeToRenderIfConditionData,
  CustomMessageNodeData,
  CustomSliderNodeData,
  MultiOption,
  CustomIfConditionNodeData,
  CustomNode,
  CustomEdge,
} from '@src/components/custom-nodes/types';
import { useAtom } from 'jotai';
import { Icon } from '@src/components/icon/Icon';
import { CustomTooltip } from '@src/components/tooltip/Tooltip';
import { AddRow } from '@src/components/node-info/behavior-rules';
import { Selectbox } from '@src/components/selectbox/Selectbox';
import { ReactflowInstanceContext } from '@src/scenario-editor/ScenarioEditor';
import { reactFlowInstanceAtom, branchUpdaterKey, wrapperSizeAtom, getOpenedInfoModalCounter } from '@src/atoms';
import { deleteStatementBranch, addStatementBranch, reorderStatementBranch, recalculateHandlePositions } from '@src/scenario-editor/render-utils';
import { recalculateNodesYPosForNodes, recalculateNodesXPos, getPrecededNodes, updateNode } from '@src/scenario-editor/utils';
import { getIFStatementSchema } from '@src/scenario-editor/validator/schemas/if-statement';
import { NODES_TO_PASTE_CONDITION_AFTER } from '@src/consts';
import {validatorInstance} from '@src/scenario-editor/validator/Validator';

const ComboboxValueRenderer = ({ value, options = [], onValueChange, children, disabled }: ValueRendererProps) => {
  return (
    <div className="w-full flex flex-wrap items-center">
      <div className="border border-input py-1 px-2 mr-2 rounded-md inline-block">Is</div>
      {value.map((option, idx) => {
        const isOptionValid = options.find((opt) => opt.value === option.value);
        const tooltipContent = isOptionValid ? option.label : 'The associated object was deleted';
        return (
          <div key={option.label} className={cn("inline-block my-1", { 'opacity-50': disabled })}>
            {idx > 0 && <div className="border border-input py-1 px-2 mr-2 rounded-md inline-block">Or</div>}
            <Badge className="mr-2 px-1 py-1 max-w-[200px] text-sm" variant="secondary" key={option.label}>
              <CustomTooltip content={tooltipContent}>
                <div className={cn("relative truncate font-light w-full", {'pl-5': !isOptionValid})}>
                  {!isOptionValid && <LinkBreak2Icon className="absolute left-0 top-1 text-red-500 mr-2"/>}
                  {option.label}
                </div>
              </CustomTooltip>
              {!disabled && (
                  <CustomTooltip content="Remove option">
                  <Cross1Icon
                    className={cn(
                      "ml-1 cursor-pointer max-w-3 min-w-3",
                    )}
                    onClickCapture={() => {
                      onValueChange?.(value.filter((opt) => opt.value !== option.value));
                    }}
                  />
                </CustomTooltip>
              )}
            </Badge>
          </div>
        )}
      )}
      {children}
    </div>
  )
}

interface MessageNodeInfoProps {
  selectedNodeProps: NodeToRenderProps;
  disabled: boolean;
}

const reorder = (list: ConditionStatement[], startIndex: number, endIndex: number) => {
  const result = [...list];
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) {
    return null;
  }
  return <Droppable {...props}>{children}</Droppable>;
};

export const IfStatementNodeInfo = ({ selectedNodeProps, disabled }: MessageNodeInfoProps) => {
  const selectedNodePropsData = selectedNodeProps?.data as NodeToRenderIfConditionData;
  const reactFlowInstance = useContext(ReactflowInstanceContext);
  const [,setBranchUpdaterKey] = useAtom(branchUpdaterKey);
  const wrapperSize = useAtom(wrapperSizeAtom)[0];

  const instance = reactFlowInstance.get(reactFlowInstanceAtom)!;
  const nodes = instance.getNodes() as CustomNode[];
  const edges = instance.getEdges() as CustomEdge[];

  const precededNodes = getPrecededNodes(selectedNodeProps, nodes, edges);
  const messageNodesWithAnswers = precededNodes
    .filter((node) => (NODES_TO_PASTE_CONDITION_AFTER.includes((node.data as CustomNodeData)?.nodeType ?? '')))
  const shouldShowErrorWarnings = getOpenedInfoModalCounter(selectedNodeProps.id) > 1;

  const FormSchema = getIFStatementSchema({
    possibleQuestions: []
  });

  interface FormValues {
    condStatements: ConditionStatement[];
    condSourceId: string;
  }

  const defaultValues: FormValues = {
    condStatements: selectedNodePropsData?.condStatements ?? [],
    condSourceId: selectedNodePropsData?.condSourceId ?? ''
  }

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues,
  });

  const { fields, remove, insert } = useFieldArray({
    control: form.control,
    name: "condStatements",
  });

  const condSourceId = form.getValues().condSourceId;
  const condSourceNodeData =
    ((instance.getNodes() as CustomNode[])
    .find((node) => node.id === condSourceId)?.data as CustomMessageNodeData);
  const sliderSourceData =
    condSourceNodeData?.nodeType === 'message:slider'
      ? (condSourceNodeData as unknown as CustomSliderNodeData)
      : null;
  const sliderOptions = (() => {
    if (!sliderSourceData) {return null;}
    const min = Number(sliderSourceData.min);
    const max = Number(sliderSourceData.max);
    const incrementRaw = Number(sliderSourceData.increment);
    const increment = Number.isFinite(incrementRaw) && incrementRaw > 0 ? incrementRaw : 1;
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {return [];}
    const opts: { label: string; value: string }[] = [];
    for (let v = min; v <= max; v += increment) {
      opts.push({ label: String(v), value: String(v) });
    }
    return opts;
  })();
  const condSourceOptions = sliderOptions ?? condSourceNodeData?.response ?? [];
  const shouldUseAnswerOptions = false;
  const updateItem = updateNode({ reactFlowInstance: instance });
  const shouldAllowManySelections = condSourceNodeData?.nodeType === 'message:multi';

  useEffect(() => {
    const subscription = form.watch(() => {
      void form.handleSubmit(changeHandler)()
      changeHandler(form.getValues());
    });

    // enable initial fields validation
    // form.trigger().catch(console.error);

    return () => subscription.unsubscribe();
  }, [form.handleSubmit, form.watch]);

  useEffect(() => {
    const possibleQuestions = condSourceOptions.map(option => option.value) as never;
    const schema = getIFStatementSchema({ possibleQuestions });

    validatorInstance.addZodRule(`if:condition:${selectedNodeProps?.id}`, schema);
  }, [condSourceOptions])

  const changeHandler = (data: FormValues) => {
    updateItem?.(selectedNodeProps?.id ?? '', {
      condStatements: data.condStatements,
      condSourceId: data.condSourceId
    } as CustomIfConditionNodeData);
    setTimeout(() => {
      setBranchUpdaterKey(() => (Math.random()));
    }, 0);
  }

  const resetCondStatement = () => {
    const formValues = form.getValues();
    const statementsValues = formValues.condStatements;
    const updatedStatementValues = statementsValues.map(({ op }) => ({ op, value: op === 'or' ? [] : null }));

    form.setValue('condStatements', updatedStatementValues);
    setTimeout(() => {
      updateItem?.(selectedNodeProps?.id ?? '', {
        condStatements: updatedStatementValues,
        condSourceId: formValues.condSourceId
      } as CustomIfConditionNodeData);
      form.trigger().catch(console.error);
    }, 0);
  }

  const removeStatementHandler = (index: number) => {
    const instance = reactFlowInstance.get(reactFlowInstanceAtom)!;
    const { updatedNodes, updatedEdges } = deleteStatementBranch(reactFlowInstance.get(reactFlowInstanceAtom)!, selectedNodeProps, index ) ?? {};
    const nodesToSave = updatedNodes!.map((node: CustomNode) =>
      node.id === selectedNodeProps.id
        ? ({
          ...node,
          data: {
            ...(node.data as CustomNodeData),
            condStatements: fields.filter((_, i) => i !== index - 1)
          }
        })
        : node
    );

    remove(index);

    recalculateNodesXPos(nodesToSave, wrapperSize.x, { instance });
    recalculateNodesYPosForNodes(nodesToSave, updatedEdges!);
    recalculateHandlePositions(nodesToSave, updatedEdges!);

    instance.setNodes(() => nodesToSave);
    instance.setEdges(() => updatedEdges!);
  }

  const addBranchHandler = () => {
    const instance = reactFlowInstance.get(reactFlowInstanceAtom)!;
    const newCondition = { op: 'or' as const, value: []};
    const { updatedNodes, updatedEdges } = addStatementBranch(reactFlowInstance.get(reactFlowInstanceAtom)!, selectedNodeProps) ?? {};
    const nodesToSave = updatedNodes.map((node: CustomNode) =>
      node.id === selectedNodeProps.id
        ? ({
          ...node,
          data: {
            ...(node.data as CustomNodeData),
            // add before last (Else) statement
            condStatements: fields.reduce((acc, {op, value}, index) => {
              if (index === fields.length - 1) {
                return acc.concat(newCondition).concat({op, value});
              }
              return acc.concat({op, value});
            }, [] as ConditionStatement[])
          }
        })
        : node
    );

    insert(fields.length - 1, newCondition)

    setTimeout(() => {
      instance.setNodes(() => nodesToSave);
      instance.setEdges(() => updatedEdges);
    }, 500);

    setTimeout(() => {
      recalculateNodesXPos(nodesToSave, wrapperSize.x, { instance });
      recalculateNodesYPosForNodes(nodesToSave, updatedEdges);
      recalculateHandlePositions(nodesToSave, updatedEdges);
    }, 500);
  }

  return (
    <div className="text-sm flex-col flex w-full">
      <Form {...form}>
        <div className="border-b border-separator p-4 flex">
          <div className="min-w-20 pl-2 text-label-foreground mt-2">Field</div>
          <div className="w-full max-w-[280px]">
            <Controller
              control={form.control}
              name="condSourceId"
              render={({ field, fieldState }) => {
                return (
                  <>
                    <Selectbox
                      options={
                        messageNodesWithAnswers.map((node) => ({
                          label: (
                            (node.data as CustomMessageNodeData)?.message
                            ? (node.data as CustomMessageNodeData)?.message
                            : (node.data as CustomMessageNodeData)?.title) ?? '',
                          value: node.id
                        }))
                      }
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        resetCondStatement();
                      }}
                      isValid={!fieldState.error}
                      placeholder="Select field..."
                      disabled={disabled}
                    />
                    {fieldState.error && shouldShowErrorWarnings && <div className="text-destructive mt-1">{fieldState.error.message}</div>}
                  </>
                )
              }}
            />
          </div>
        </div>

        <DragDropContext onDragEnd={(result: DropResult) => {
          if (!result.destination) {
            return;
          }
          // Prevent dropping after the 'else' node
          const destinationField = fields[result.destination.index];
          if (destinationField?.op === 'else') {
            return;
          }
          const items = reorder(
            fields,
            result.source.index,
            result.destination.index
          );
          form.setValue('condStatements', items);

          const reorderResult = reorderStatementBranch({
            instance: reactFlowInstance.get(reactFlowInstanceAtom)!,
            node: selectedNodeProps,
            branchIndex: result.source.index,
            newPositionIndex: result.destination.index
          });

          if (!reorderResult) return;

          const { updatedNodes } = reorderResult;
          const nodesToSave = updatedNodes.map(node =>
            node.id === selectedNodeProps?.id
              ? ({
                ...node,
                data: {
                  ...(node.data as CustomNodeData),
                  condStatements: items
                }
              })
              : node
          );

          setTimeout(() => {
            instance.setNodes(() => nodesToSave);
            instance.setEdges(() => edges);
          }, 100);
        }}>
          <StrictModeDroppable droppableId="droppable">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {fields.slice(0, -1).map((field, index) => (
                  <Draggable key={field.id} draggableId={field.id} isDragDisabled={disabled} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="flex w-full items-start border-b border-separator p-4"
                        style={{
                          ...provided.draggableProps.style,
                          left: undefined,
                          top: undefined,
                        }}
                      >
                        {!disabled && (
                          <div {...provided.dragHandleProps} className="cursor-move absolute -ml-[24px] mt-[5px] ">
                            <Icon type="drag-handler" className="mr-2" />
                          </div>
                        )}
                        <div className="min-w-20 pt-2 pl-2 text-label-foreground">{index + 1}</div>
                        <div className="grow">
                          <Controller
                            control={form.control}
                            name={`condStatements.${index}`}
                            render={({ field }) => {
                              const { onChange, value } = field;
                              let comboboxValue = (value?.value ?? []).map(item => {
                                const option = condSourceOptions?.find(option => option.value === item.value);
                                return ({
                                  value: item.value,
                                  label: option?.label ?? item.value
                                })
                              });
                              if (shouldUseAnswerOptions) {
                                comboboxValue = comboboxValue.filter(item => condSourceOptions?.find(option => option.value === item.value));
                              }
                              const valueChangeHandler = (newValue: Option[]) => {
                                onChange({
                                  op: value.op,
                                  value: newValue.map(item => ({
                                    op: 'eq',
                                    value: item.value
                                  }))

                                })
                              }

                              // already selected values by previous statements
                              const condStatements = form.getValues().condStatements;
                              const alreadySelectedValues = condStatements.slice(0, condStatements.length - 1)
                                .reduce((acc, { value }) => acc.concat(value as never), [])
                                .map(({ value }) => value) as string[];

                              let currentValuesSelectedHere: MultiOption[] = [];
                              if (value.op === 'or') {
                                currentValuesSelectedHere = (value.value ?? [])
                                  .map(({ value }) => {
                                    const option = condSourceOptions?.find(option => option.value === value);
                                    if (!option) {
                                      return null;
                                    }
                                    return ({ value, label: option?.label })
                                  })
                                  .filter(Boolean) as MultiOption[];
                              }

                              let condSourceOptionsWithoutSelected =
                                (condSourceOptions ?? [])
                                  ?.filter(option => !alreadySelectedValues.includes(option.value))
                                  ?.concat(currentValuesSelectedHere);

                              if (shouldAllowManySelections) {
                                condSourceOptionsWithoutSelected = condSourceOptions;
                              }

                              const condSourceOptionsSorted = condSourceOptionsWithoutSelected?.sort((a, b) => a.label.localeCompare(b.label));

                              return (
                                <div className="py-2 grow">
                                  {value?.op === 'or' && (
                                    <MessageCombobox
                                      options={condSourceOptionsSorted}
                                      value={comboboxValue}
                                      disabled={!condSourceId || disabled}
                                      ignoredValues={alreadySelectedValues}
                                      onValueChange={valueChangeHandler}
                                      Renderer={ComboboxValueRenderer}
                                    />
                                  )}
                                  {value?.op === 'else' && (
                                    <div className={cn("border border-input py-1 px-2 rounded-md inline-block bg-input", { 'opacity-50': disabled })}>Else</div>
                                  )}
                                  {form.formState.errors.condStatements?.[index]?.value && shouldShowErrorWarnings &&
                                    <div className="text-destructive mt-1">
                                      {form.formState.errors.condStatements?.[index]?.value?.message}
                                    </div>
                                  }
                                </div>
                              );
                            }}
                          />
                        </div>
                        {index !== 0 && index !== fields.length - 1 && !disabled && (
                          <CustomTooltip content="Remove branch">
                            <Button variant="ghost" className="w-8 ml-2" type="button" onClick={() => removeStatementHandler(index)}>
                              <Icon type="delete" />
                            </Button>
                          </CustomTooltip>
                        )}
                        </div>
                      )}
                    </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </StrictModeDroppable>
    </DragDropContext>
    {/* Render Else node separately outside drag context */}
    {fields.length > 0 && fields[fields.length - 1].op === 'else' && (
      <div className="flex w-full items-start border-b border-separator p-4">
        <div className="min-w-20 pt-2 pl-2 text-label-foreground">{fields.length}</div>
        <div className="grow">
          <div className={cn("border border-input py-1 px-2 rounded-md inline-block bg-input", { 'opacity-50': disabled })}>Else</div>
        </div>
      </div>
    )}
          {!disabled && (
            <div className="px-4 pb-4">
              <AddRow
                label="Add branch"
                testId="info=>if:branch:add"
                onClick={() => addBranchHandler()}
              />
            </div>
          )}
      </Form>
    </div>
  );
}
